import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { GridPageLayout } from '@lux/grid';
import { configShape, withConfig } from '@browsing/config';

import { getClientDeviceWidth } from '@client/redux/modules/clientDevice/selectors';
import { APPDOMAIN_TO_TAB_ID_MAP } from '@client/config/constants';
import { getBannerDesktopImage, getBannerMobileImage } from '@client/helpers/images';
import { isXsScreen, isSmScreen } from '@client/helpers/screens';
import { M1_REBRANDING } from '@client/components/mylounge/constants';

import { Image } from './banner-slider.styles';
import BannerContent from './banner-content';
import { ANIMATION_WAIT } from './constants';

export class BannerSlider extends PureComponent {
    static propTypes = {
        config: configShape,
        tabs: PropTypes.array.isRequired,
        activeIndex: PropTypes.number.isRequired,
        lastIndex: PropTypes.number.isRequired,
        tabImages: PropTypes.object.isRequired,
        deviceWidth: PropTypes.number,
        onImageClick: PropTypes.func.isRequired,
    };

    constructor(props) {
        super(props);

        this.state = {
            movement: {
                start: 0,
                current: 0,
                limit: 0,
            },
            animationStyles: {},
        };

        this.bannerRef = React.createRef();
    }

    render() {
        const { tabs, activeIndex, lastIndex, onImageClick, config } = this.props;
        const { movedIndex, animationStyles } = this.state;
        const isM1Rebranding = config.isEnabled(M1_REBRANDING);

        return (
            <div ref={this.bannerRef}>
                {tabs.map((tab, tabIndex) => (
                    <Image
                        id={`thb-${tab?.thb?.id}`}
                        data-position={tabIndex + 1}
                        key={tab.id}
                        role="tabpanel"
                        aria-hidden={tabIndex !== activeIndex}
                        aria-label={tab?.thb?.text}
                        style={{
                            backgroundImage: this.shouldLoadImage(tabIndex)
                                ? this.getBannerImageUrl(tab)
                                : '',
                            ...(animationStyles[tabIndex] || {}),
                        }}
                        animated={
                            tabIndex === activeIndex ||
                            tabIndex === lastIndex ||
                            tabIndex === movedIndex
                        }
                        clickable={tab?.thb?.id || tab?.thb?.url}
                        active={tabIndex === activeIndex}
                        next={tabIndex > activeIndex}
                        previous={tabIndex < activeIndex}
                        lastChance={this.isLastChanceTab(tab)}
                        onTouchStart={this.handleSlideMoveStart}
                        onTouchMove={this.handleSlideMove}
                        onTouchEnd={this.handleSlideMoveEnd}
                        onClick={onImageClick}
                        onKeyPress={onImageClick}
                        isM1Rebranding={isM1Rebranding}
                    >
                        <GridPageLayout>
                            <BannerContent
                                active={tabIndex === activeIndex}
                                thb={tab.thb}
                                onButtonClick={onImageClick}
                            />
                        </GridPageLayout>
                    </Image>
                ))}
            </div>
        );
    }

    shouldLoadImage = tabIndex => {
        const { tabImages, activeIndex } = this.props;

        return tabIndex === activeIndex || tabImages[tabIndex];
    };

    isLastChanceTab = tab => {
        const { config } = this.props;

        return tab.id === APPDOMAIN_TO_TAB_ID_MAP[config.appdomainid];
    };

    getBannerImage = tab => {
        const { deviceWidth } = this.props;
        const isLastChanceTab = this.isLastChanceTab(tab);

        if (!isXsScreen(deviceWidth) || isLastChanceTab) {
            return getBannerDesktopImage(tab);
        }

        return getBannerMobileImage(tab);
    };

    getBannerImageUrl = tab => {
        const image = this.getBannerImage(tab);

        return image ? `url(${image})` : '';
    };

    handleSlideMoveStart = event => {
        if (!event.touches || !event.touches.length) {
            return;
        }
        const touch = event.touches[0];
        const bannerElem = this.bannerRef.current;
        const { width } = bannerElem.getBoundingClientRect();
        const limit = width * 0.4;
        this.setState({
            movement: {
                start: touch.clientX,
                current: touch.clientX,
                limit,
            },
        });
    };

    handleSlideMove = event => {
        if (!event.touches || !event.touches.length) {
            return;
        }

        const touch = event.touches[0];
        const {
            movement: { limit, start },
        } = this.state;
        const { deviceWidth } = this.props;
        const current = touch.clientX;
        const movementPerc = Math.floor(((start - current) / deviceWidth) * 100);
        this.updateAnimationStyles(movementPerc);

        this.setState({
            movement: {
                start,
                current,
                limit,
            },
            movementPerc,
        });
    };

    handleSlideMoveEnd = event => {
        const { movement } = this.state;
        const { activeIndex, tabs, deviceWidth } = this.props;
        const movementPerc = (movement.start - movement.current) / movement.limit;
        const timeout = isSmScreen(deviceWidth) ? ANIMATION_WAIT : 0;

        const slideToChangeTo = this.getSlideToChangeTo(movementPerc, activeIndex, tabs.length - 1);
        this.resetMovement();

        if (slideToChangeTo === null) {
            return;
        }

        if (event.cancelable) {
            event.preventDefault();
        }

        this.props.onBannerSwipe(slideToChangeTo, timeout);
    };

    resetMovement() {
        const { movedIndex } = this.state;
        this.setState({
            movement: {
                start: 0,
                current: 0,
                limit: 0,
            },
            movementPerc: 0,
            animationStyles: {},
        });

        this.clearTimeout();
        if (movedIndex !== null) {
            this.movedIndexTimeout = setTimeout(() => {
                this.setState({ movedIndex: null });
            }, ANIMATION_WAIT);
        }
    }

    clearTimeout = () => {
        if (this.movedIndexTimeout) {
            clearTimeout(this.movedIndexTimeout);
            this.movedIndexTimeout = null;
        }
    };

    getSlideToChangeTo(percantage, activeIndex, max) {
        const threshold = 0.3;

        if (percantage > threshold) {
            const nextSlide = activeIndex + 1;

            return nextSlide > max ? activeIndex : nextSlide;
        }

        if (percantage < threshold * -1) {
            const prevSlide = activeIndex - 1;

            return prevSlide < 0 ? activeIndex : prevSlide;
        }

        return null;
    }

    updateAnimationStyles = movementPerc => {
        const active = this.getActiveLeftStyle(movementPerc);
        const other = this.getOtherLeftStyle(movementPerc);

        this.setState({
            animationStyles: {
                [active.id]: { left: `${active.left}%`, transition: 'none' },
                [other.id]: { left: `${other.left}%`, transition: 'none' },
            },
        });

        this.updateMovedIndex(other);
    };

    updateMovedIndex(other) {
        if (!other.left || Math.abs(other.left) === 100) {
            return;
        }

        this.setState({ movedIndex: other.id });
    }

    getActiveLeftStyle = movementPerc => {
        if (!movementPerc) {
            return {};
        }
        const { activeIndex } = this.props;

        return {
            id: activeIndex,
            left: -movementPerc,
        };
    };

    getOtherLeftStyle = movementPerc => {
        if (!movementPerc) {
            return {};
        }

        const { activeIndex } = this.props;
        const tabIndex = movementPerc < 0 ? activeIndex - 1 : activeIndex + 1;
        const diff = tabIndex - activeIndex;
        const directionActive = diff > 0 ? movementPerc > 0 : movementPerc < 0;
        const leftValue =
            Math.sign(diff) * (100 - Math.abs(movementPerc) * Number(directionActive));

        return {
            id: tabIndex,
            left: leftValue,
        };
    };

    componentWillUnmount() {
        this.clearTimeout();
    }
}

const mapStateToProps = state => ({
    deviceWidth: getClientDeviceWidth(state),
});

export default compose(withConfig, connect(mapStateToProps))(BannerSlider);
